import { prepareMermaidSvgForViewer, renderMermaidDiagram } from '../components/LensIntelligence/markdown/mermaidLoader';
import { markdownToJournalHtml } from './markdownToJournalHtml';

const FENCE_OPEN = /^ {0,3}```([\w-]*)(?:[ \t].*)?$/;
const FENCE_CLOSE = /^ {0,3}```[ \t]*$/;
const MAX_RASTER_WIDTH = 1600;

function escapeHtml(text) {
    return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function formatInline(text) {
    const html = markdownToJournalHtml(String(text || '').replace(/\n/g, ' '));
    return html.replace(/^<p>/, '').replace(/<\/p>$/, '');
}

function splitTableRow(line) {
    let row = String(line || '').trim();
    if (row.startsWith('|')) row = row.slice(1);
    if (row.endsWith('|')) row = row.slice(0, -1);
    return row.split('|').map(cell => cell.trim());
}

function isSeparatorRow(line) {
    const cells = splitTableRow(line);
    return cells.length > 0 && cells.every(cell => /^:?-{3,}:?$/.test(cell));
}

function parseAlign(cell) {
    const left = cell.startsWith(':');
    const right = cell.endsWith(':');
    if (left && right) return 'center';
    if (right) return 'right';
    return 'left';
}

function looksLikeTableRow(line) {
    if (!line || !String(line).includes('|')) return false;
    const trimmed = String(line).trim();
    if (trimmed.startsWith('```')) return false;
    return splitTableRow(trimmed).length >= 2;
}

function padCells(cells, length) {
    const next = cells.slice(0, length);
    while (next.length < length) next.push('');
    return next;
}

function renderTableHtml(headers, aligns, rows) {
    const colCount = Math.max(headers.length, 1);
    const headerCells = padCells(headers, colCount)
        .map((cell, index) => {
            const align = aligns[index] || 'left';
            return `<th style="text-align:${align}">${formatInline(cell)}</th>`;
        })
        .join('');
    const body = rows
        .map(row => {
            const cells = padCells(row, colCount)
                .map((cell, index) => {
                    const align = aligns[index] || 'left';
                    return `<td style="text-align:${align}">${formatInline(cell)}</td>`;
                })
                .join('');
            return `<tr>${cells}</tr>`;
        })
        .join('');
    return `<table class="doc-table"><thead><tr>${headerCells}</tr></thead><tbody>${body}</tbody></table>`;
}

function renderCodeHtml(language, code) {
    const lang = String(language || '').trim();
    const langAttr = lang ? ` data-lang="${escapeHtml(lang)}"` : '';
    return `<pre class="doc-code"${langAttr}><code>${escapeHtml(code)}</code></pre>`;
}

function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Failed to load SVG image'));
        img.src = src;
    });
}

function readSvgSize(svgEl) {
    const viewBox = svgEl.getAttribute('viewBox');
    if (viewBox) {
        const parts = viewBox.trim().split(/[\s,]+/).map(Number);
        if (parts.length === 4 && parts[2] > 0 && parts[3] > 0) {
            return { width: parts[2], height: parts[3] };
        }
    }
    const width = parseFloat(svgEl.getAttribute('width'));
    const height = parseFloat(svgEl.getAttribute('height'));
    if (width > 0 && height > 0) return { width, height };
    return { width: 800, height: 400 };
}

export async function rasterizeSvgForPdf(svgMarkup) {
    const prepared = prepareMermaidSvgForViewer(svgMarkup);
    const doc = new DOMParser().parseFromString(prepared, 'image/svg+xml');
    const svgEl = doc.documentElement;
    if (!svgEl || svgEl.nodeName.toLowerCase() === 'parsererror') {
        throw new Error('Invalid mermaid SVG');
    }
    if (!svgEl.getAttribute('xmlns')) {
        svgEl.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    }

    const { width, height } = readSvgSize(svgEl);
    svgEl.setAttribute('width', String(width));
    svgEl.setAttribute('height', String(height));

    const xml = new XMLSerializer().serializeToString(svgEl);
    const blob = new Blob([xml], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    try {
        let img;
        try {
            img = await loadImage(url);
        } catch {
            img = await loadImage(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`);
        }
        const scale = Math.min(2, MAX_RASTER_WIDTH / Math.max(width, 1));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(width * scale));
        canvas.height = Math.max(1, Math.round(height * scale));
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        return canvas.toDataURL('image/png');
    } finally {
        URL.revokeObjectURL(url);
    }
}

async function renderMermaidHtml(code, index, rasterizeSvg) {
    const source = String(code || '').trim();
    if (!source) return '';
    try {
        const renderId = `pdf-mermaid-${Date.now()}-${index}`;
        const { svg } = await renderMermaidDiagram(renderId, source);
        if (!svg) throw new Error('Empty mermaid SVG');
        const dataUrl = await rasterizeSvg(svg);
        if (!dataUrl) throw new Error('Empty mermaid image');
        return `<figure class="doc-mermaid"><img src="${dataUrl}" alt="Diagram" /></figure>`;
    } catch {
        return `<figure class="doc-mermaid-fallback"><figcaption>Diagram</figcaption>${renderCodeHtml(
            'mermaid',
            source,
        )}</figure>`;
    }
}

function readFence(lines, startIndex) {
    const open = String(lines[startIndex] || '').match(FENCE_OPEN);
    if (!open) return null;

    const language = String(open[1] || '').trim().toLowerCase();
    const body = [];
    let index = startIndex + 1;
    while (index < lines.length && !FENCE_CLOSE.test(lines[index])) {
        body.push(lines[index]);
        index += 1;
    }
    const closed = index < lines.length;
    if (closed) index += 1;
    return { language, code: body.join('\n'), endIndex: index };
}

function readTable(lines, startIndex) {
    if (startIndex + 1 >= lines.length) return null;
    if (!looksLikeTableRow(lines[startIndex]) || !isSeparatorRow(lines[startIndex + 1])) return null;

    const headers = splitTableRow(lines[startIndex]);
    const aligns = splitTableRow(lines[startIndex + 1]).map(parseAlign);
    const rows = [];
    let index = startIndex + 2;
    while (index < lines.length && looksLikeTableRow(lines[index]) && !isSeparatorRow(lines[index])) {
        rows.push(splitTableRow(lines[index]));
        index += 1;
    }
    return { headers, aligns, rows, endIndex: index };
}

export function tokenizeMarkdownForPdf(markdown) {
    const lines = String(markdown || '').replace(/\r\n/g, '\n').split('\n');
    const segments = [];
    let textLines = [];
    let index = 0;

    const flushText = () => {
        const text = textLines.join('\n').trim();
        textLines = [];
        if (text) segments.push({ type: 'text', text });
    };

    while (index < lines.length) {
        const fence = readFence(lines, index);
        if (fence) {
            flushText();
            segments.push({
                type: fence.language === 'mermaid' ? 'mermaid' : 'code',
                language: fence.language,
                code: fence.code,
            });
            index = fence.endIndex;
            continue;
        }

        const table = readTable(lines, index);
        if (table) {
            flushText();
            segments.push({
                type: 'table',
                headers: table.headers,
                aligns: table.aligns,
                rows: table.rows,
            });
            index = table.endIndex;
            continue;
        }

        textLines.push(lines[index]);
        index += 1;
    }

    flushText();
    return segments;
}

/**
 * Convert Ask Lens markdown into HTML that PDF export can capture:
 * GFM tables as real tables, mermaid fences as raster images.
 */
export async function markdownToPdfHtml(markdown, { rasterizeSvg = rasterizeSvgForPdf } = {}) {
    const segments = tokenizeMarkdownForPdf(markdown);
    const parts = [];
    let mermaidIndex = 0;

    for (const segment of segments) {
        if (segment.type === 'table') {
            parts.push(renderTableHtml(segment.headers, segment.aligns, segment.rows));
            continue;
        }
        if (segment.type === 'mermaid') {
            parts.push(await renderMermaidHtml(segment.code, mermaidIndex, rasterizeSvg));
            mermaidIndex += 1;
            continue;
        }
        if (segment.type === 'code') {
            parts.push(renderCodeHtml(segment.language, segment.code));
            continue;
        }
        parts.push(markdownToJournalHtml(segment.text));
    }

    return parts.join('');
}
